import { cn } from "@/lib/utils";
import React from "react";

interface FolderNameProps {
  /** Folder name to display */
  name: string;
  /** Whether the folder name is being edited */
  isEditing: boolean;
  /** Called when the input value changes */
  onChange?: (value: string) => void;
  /** Called when editing is confirmed (Enter key or blur) */
  onConfirm?: (value: string) => void;
  /** Called when editing is cancelled (Escape key) */
  onCancel?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FolderName - Atom component
 *
 * Displays folder name with text truncation or editable input field.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderName name="Documents" isEditing={false} />
 * <FolderName name="Long folder name" isEditing={true} onChange={handleChange} onConfirm={handleConfirm} />
 * ```
 */
export const FolderName = React.memo(function FolderName({
  name,
  isEditing,
  onChange,
  onConfirm,
  onCancel,
  className,
}: FolderNameProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onConfirm?.(e.currentTarget.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel?.();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onConfirm?.(e.currentTarget.value);
  };

  if (isEditing) {
    return (
      <input
        type="text"
        value={name}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        autoFocus
        className={cn(
          "h-6 rounded-md border border-slate-300 bg-white px-2 text-sm font-medium text-slate-900",
          "shadow-subtle transition-all duration-200",
          "focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
          "placeholder:text-slate-400",
          className
        )}
        aria-label="Edit folder name"
      />
    );
  }

  return (
    <span
      className={cn(
        "block truncate text-sm font-medium text-slate-700 transition-colors duration-150",
        "group-hover:text-slate-900",
        className
      )}
      title={name}
      aria-label={`Folder name: ${name}`}
    >
      {name}
    </span>
  );
});
